import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DollarSign, MinusCircle, Box, BarChart3, CreditCard, ChevronRight, Info } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { Spinner, PageTitle } from "@/components/earnos/kit";
import { colors, money } from "@/lib/theme";

function MenuRow({ icon: Icon, tint, label, sub, onClick, testID }) {
  return (
    <button onClick={onClick} data-testid={testID} className="w-full flex items-center gap-3 rounded-xl border border-line bg-surface-2 p-4 hover:border-line-strong transition-colors text-left">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: tint + "22" }}>
        <Icon size={20} style={{ color: tint }} />
      </div>
      <div className="flex-1 min-w-0"><p className="text-base font-bold text-ink">{label}</p><p className="text-sm text-ink-2">{sub}</p></div>
      <ChevronRight size={20} className="text-ink-2" />
    </button>
  );
}

export default function Money() {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { setP(await apiFetch("/portfolio")); setError(""); } catch (e) { setError(e.message || "Could not load your financial records."); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="animate-fade-in">
      <PageTitle>Money</PageTitle>

      {loading ? (
        <Spinner className="py-16" />
      ) : error ? <div role="alert" className="text-danger mt-5">{error} <button onClick={load} className="underline font-bold">Retry</button></div> : (
        <div className="rounded-2xl p-6 mt-5 bg-gradient-to-br from-[#059669] to-[#0F766E] text-white">
          <p className="text-base font-semibold text-white/85">User-reported received</p>
          <p className="text-4xl font-extrabold tracking-tight mt-1">{money(p?.total_revenue_received || 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          <p className="text-sm text-white/85 mt-2">Estimated net {money(p?.net_estimated || 0)} · {p?.won_customers || 0} customers won</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3 mt-5">
        <MenuRow icon={DollarSign} tint={colors.success} label="Earnings & Transactions" sub="Revenue received, pending, refunds" onClick={() => navigate("/earnings")} testID="money-earnings" />
        <MenuRow icon={MinusCircle} tint={colors.warning} label="Expenses & Fees" sub="Track expenses, platform & processing fees" onClick={() => navigate("/earnings")} testID="money-expenses" />
        <MenuRow icon={Box} tint={colors.brand} label="Income Assets" sub="Products, templates & recurring income" onClick={() => navigate("/income-assets")} testID="money-assets" />
        <MenuRow icon={BarChart3} tint={colors.brandTertiary} label="Income Portfolio" sub="All your engines and performance" onClick={() => navigate("/portfolio")} testID="money-portfolio" />
        <MenuRow icon={CreditCard} tint={colors.brandSecondary} label="Membership & Fees" sub="Plan, EARNOS fees & payment history" onClick={() => navigate("/billing")} testID="money-billing" />
      </div>

      <div className="flex items-start gap-1.5 mt-8">
        <Info size={14} className="text-ink-2 mt-0.5 shrink-0" />
        <p className="text-sm text-ink-2 leading-tight max-w-2xl">User-reported financial information. EARNOS does not verify all earnings and is not a bank account.</p>
      </div>
    </div>
  );
}
